import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { matchesQuery } from '../brandSelection'
import { useFixedPanel, useOverlayPopover } from '../useOverlayPopover'

// MP-OS-189 — the ONE single-select picker for every list that can grow past ~10 options
// (suppliers, brands, articles, weeks, flavours …). Short fixed lists (granularity, strength,
// format, a 3–5 option segment choice) stay native <select>s.
//
// - A button shows the current value; its accessible name is "<label>: <current>", so the
//   selected value is announced. Opening it shows an overlay (fixed, nothing moves) with a
//   search field (combobox) above the list (listbox).
// - Type to filter on the display name, case- and accent-insensitively (matchesQuery).
// - Keyboard: ↓/↑ (wrapping), Ctrl+Home/Ctrl+End jump to the first/last option, Enter picks,
//   Tab closes; plain Home/End move the text cursor in the search field (the ARIA editable
//   combobox pattern, MPD-84 M71-2). Typing on the closed
//   button opens it with that letter. Escape, outside click and focus return are the shared
//   popover behaviour (useOverlayPopover → usePopoverDismiss). The mouse works as well.
// - "No matches" says so and offers the full list back.
// - `disabled` and `loading` disable the button; `loading` sets aria-busy and keeps showing the
//   chosen value's label (MPD-84 M71-4); `loadingText` shows only when no option matches it.
// - onChange fires only for a different value, like a native <select>.
//
// The multi-select BrandPicker (Sales Trends) stays separate: it toggles several brands
// with a competitor cap and groups own/market brands. It shares the search rule (matchesQuery)
// and the Escape/focus behaviour (usePopoverDismiss), not this component.

export type SelectOption = { value: string; label: string }

export function SearchableSelect({
  label,
  options,
  value,
  onChange,
  searchLabel = 'Search',
  showAllLabel = 'Show all',
  placeholder,
  disabled = false,
  loading = false,
  loadingText = 'Loading…',
  required = false,
  title,
  className,
  triggerClassName = 'control-select ss-trigger',
  style,
  adornment,
}: {
  /** Accessible name of the picker, e.g. "Filter by brand". */
  label: string
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  /** Accessible name and placeholder of the search field, e.g. "Search brands". */
  searchLabel?: string
  /** The reset button in the "No matches" state. */
  showAllLabel?: string
  /** Shown on the button when `value` is not one of the options. */
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  required?: boolean
  title?: string
  className?: string
  triggerClassName?: string
  style?: CSSProperties
  /** Custom face (e.g. the supplier pill); the button then becomes a transparent overlay. */
  adornment?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [minWidth, setMinWidth] = useState<number>()
  const wrapRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listId = useId()

  const close = () => setOpen(false)
  const { dismiss } = useOverlayPopover({ open, close, triggerRef, containerRef: wrapRef })
  useFixedPanel({ open, close, triggerRef, panelRef })

  const selected = options.find((o) => o.value === value)
  // MPD-84 M71-4: while loading, the choice keeps the label it had before the reload, even when
  // the reloading list no longer holds it (an idempotent write of what was last shown or picked).
  const lastShown = useRef<SelectOption | null>(null)
  if (!loading && selected) lastShown.current = selected
  const kept = loading && lastShown.current?.value === value ? lastShown.current : selected
  const shownLabel = kept?.label ?? (loading ? loadingText : placeholder ?? value)
  const shown = query.trim() ? options.filter((o) => matchesQuery(o.label, query)) : options
  const active = Math.min(activeIndex, shown.length - 1)
  const unavailable = disabled || loading

  useEffect(() => {
    if (!open) return
    const input = inputRef.current
    if (!input) return
    input.focus()
    input.setSelectionRange?.(input.value.length, input.value.length)
  }, [open])

  useEffect(() => {
    if (!open || active < 0) return
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active])

  // A picker that becomes unavailable while open (its data reloads) closes.
  useEffect(() => { if (unavailable) setOpen(false) }, [unavailable])

  const openMenu = (initialQuery = '') => {
    if (unavailable) return
    setQuery(initialQuery)
    setActiveIndex(initialQuery ? 0 : Math.max(0, options.findIndex((o) => o.value === value)))
    setMinWidth(triggerRef.current?.getBoundingClientRect().width || undefined)
    setOpen(true)
  }
  const pick = (o: SelectOption) => {
    lastShown.current = o // a pick that starts a reload keeps its label (M71-4)
    dismiss()
    if (o.value !== value) onChange(o.value)
  }
  const resetSearch = () => {
    setQuery('')
    setActiveIndex(0)
    inputRef.current?.focus()
  }

  return (
    <span className={className ?? 'searchable-select'} ref={wrapRef} data-open={open || undefined}>
      {adornment}
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        style={style}
        title={title}
        disabled={unavailable}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${shownLabel}`}
        aria-required={required || undefined}
        aria-busy={loading || undefined}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => {
          if (open) return
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openMenu() }
          else if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); openMenu(e.key) }
        }}
      >
        {!adornment && (
          <>
            <span className="ss-value">{shownLabel}</span>
            <ChevronDown size={14} className="ss-caret" aria-hidden />
          </>
        )}
      </button>
      {open && (
        <div className="ss-menu" ref={panelRef} style={{ minWidth }}>
          <div className="ss-search">
            <input
              ref={inputRef}
              className="ss-search-input"
              role="combobox"
              aria-label={searchLabel}
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
              placeholder={`${searchLabel}…`}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0) }}
              onKeyDown={(e) => {
                const last = shown.length - 1
                if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(shown.length ? (active + 1) % shown.length : 0) }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(shown.length ? (active - 1 + shown.length) % shown.length : 0) }
                // Plain Home/End stay with the text field (cursor); Ctrl+Home/End move the option.
                else if (e.key === 'Home' && e.ctrlKey) { e.preventDefault(); setActiveIndex(0) }
                else if (e.key === 'End' && e.ctrlKey) { e.preventDefault(); setActiveIndex(Math.max(0, last)) }
                // preventDefault also keeps Enter from submitting a surrounding form.
                else if (e.key === 'Enter') { e.preventDefault(); if (active >= 0) pick(shown[active]) }
                else if (e.key === 'Tab') close()
              }}
            />
            {query && (
              <button type="button" className="ss-search-clear" aria-label="Clear search" onClick={resetSearch}>
                <X size={13} aria-hidden />
              </button>
            )}
          </div>
          <ul className="ss-list" id={listId} ref={listRef} role="listbox" aria-label={label}>
            {shown.map((o, i) => {
              const isSelected = o.value === value
              return (
                <li
                  key={`${o.value}\u0000${i}`}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSelected}
                  className={`ss-option${isSelected ? ' ss-option--selected' : ''}${i === active ? ' ss-option--focus' : ''}`}
                  // mousedown keeps focus in the search field (no blur flicker); click picks.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseMove={() => { if (i !== active) setActiveIndex(i) }}
                  onClick={() => pick(o)}
                >
                  <span className="ss-option-label">{o.label}</span>
                  {isSelected && <Check size={14} aria-hidden className="ss-option-check" />}
                </li>
              )
            })}
          </ul>
          {shown.length === 0 && (
            <div className="ss-empty" role="status">
              No matches for “{query.trim()}”.
              <button type="button" onClick={resetSearch}>{showAllLabel}</button>
            </div>
          )}
        </div>
      )}
    </span>
  )
}
